import * as React from "react";
import { useState } from "react";
import { Database } from "lucide-react";
import { getAvailableFinsterQueryDataSourceGroups } from "../api/finster/constants";
import type { FeatureFlags, FinsterQueryDataSource } from "../api/finster/constants";
import type { ComposerAttachmentMenuAction } from "./ComposerAttachmentPicker";
import {
  DataSourcesBrowserPanel,
  getDataSourcesStatusLabel,
  type DataSourcesBrowserCloseReason,
} from "./DataSourcesBrowserPanel";

function closeOptions(reason: DataSourcesBrowserCloseReason | undefined) {
  if (reason === "back") {
    return { restoreFocus: false, reopenMenu: true };
  }
  if (reason === "dismiss") {
    return { restoreFocus: false };
  }
  return { restoreFocus: true };
}

export interface DataSourcesPickerMenuActionOptions {
  selectedDataSources: readonly FinsterQueryDataSource[];
  onDataSourcesChange: (dataSources: FinsterQueryDataSource[]) => void;
  featureFlags?: FeatureFlags;
  featureFlagsLoading?: boolean;
}

/**
 * Shared plus-menu Data Sources row for Excel and PowerPoint. Hosts own the
 * selection state and feature flags; this hook owns panel open state and the
 * Off / All / count label.
 */
export function useDataSourcesPickerMenuAction(
  options: DataSourcesPickerMenuActionOptions
): ComposerAttachmentMenuAction {
  const [pickerOpen, setPickerOpen] = useState(false);

  const {
    selectedDataSources,
    onDataSourcesChange,
    featureFlags,
    featureFlagsLoading = false,
  } = options;

  const availableDataSources = getAvailableFinsterQueryDataSourceGroups(
    featureFlags,
    featureFlagsLoading
  ).flatMap((group) => group.children.map((child) => child.param));
  const status = getDataSourcesStatusLabel(selectedDataSources, availableDataSources);

  return {
    id: "data-sources",
    label: `Data Sources (${status})`,
    icon: <Database aria-hidden />,
    trailingChevron: true,
    secondaryPanel: {
      open: pickerOpen,
      onOpenChange: setPickerOpen,
      render: ({ anchorRef, onOpenChange: closePanel }) => (
        <DataSourcesBrowserPanel
          open={pickerOpen}
          selectedDataSources={selectedDataSources}
          onDataSourcesChange={onDataSourcesChange}
          featureFlags={featureFlags}
          featureFlagsLoading={featureFlagsLoading}
          anchorRef={anchorRef}
          onOpenChange={(open, reason) => closePanel(open, closeOptions(reason))}
        />
      ),
    },
  };
}
